import { useEffect, useState } from "react";
import { Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { http, API, errMsg } from "@/lib/api";

export const AdminReviews = ({ adminKey }) => {
  const headers = { "X-Admin-Key": adminKey };
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => http.get("/reviews").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("text", text);
      fd.append("rating", rating);
      if (file) fd.append("photo", file);
      await http.post("/admin/reviews", fd, { headers });
      setName(""); setText(""); setRating(5); setFile(null);
      e.target.reset();
      toast.success("Avis ajouté");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await http.delete(`/admin/reviews/${id}`, { headers });
      setList((l) => l.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-reviews">
      <form onSubmit={submit} className="lg:col-span-4 bg-white rounded-2xl border border-[#DDD3C6] p-6 space-y-4">
        <h3 className="font-display text-2xl">Ajouter un avis / une photo</h3>
        <Input placeholder="Nom (facultatif)" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="review-name-input" />
        <Textarea rows={3} placeholder="Avis du client (facultatif)" value={text} onChange={(e) => setText(e.target.value)} data-testid="review-text-input" />
        <div className="flex gap-1" data-testid="review-rating">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" onClick={() => setRating(i)} data-testid={`review-rating-${i}`}>
              <Star size={22} className={i <= rating ? "fill-[#7B5338] text-[#7B5338]" : "text-[#CDC0B0]"} />
            </button>
          ))}
        </div>
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} className="text-sm" data-testid="review-photo-input" />
        <button className="btn-gold w-full justify-center" disabled={busy} data-testid="review-submit-button">{busy ? <Loader2 size={16} className="animate-spin" /> : "Publier"}</button>
      </form>
      <div className="lg:col-span-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.length === 0 && <p className="text-[#9A8C7D]">Aucun avis publié.</p>}
        {list.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-[#DDD3C6] overflow-hidden" data-testid={`admin-review-${r.id}`}>
            {r.has_photo && <img src={`${API}/reviews/${r.id}/photo`} alt="" className="w-full aspect-[4/3] object-cover" />}
            <div className="p-4 space-y-1">
              <div className="flex justify-between items-center">
                <p className="font-medium">{r.name}</p>
                <button onClick={() => remove(r.id)} className="text-[#9A8C7D] hover:text-red-700" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={16} /></button>
              </div>
              <p className="text-xs text-[#7B5338]">{"★".repeat(r.rating)}</p>
              <p className="text-sm text-[#6B5F53] line-clamp-3">{r.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
